/**
 * StorageStatsTab Component
 * Storage statistics and analytics
 */

import React, { useState, useEffect } from 'react';
import { Stack, Group, Grid } from '@/components/ui/layout';
import { Card, Table } from '@/components/ui/data-display';
import { RingProgress } from '@mantine/core';
import { Text, Badge } from '@/components/ui/core';
import { StorageStatCard } from '@/components/ccms-components/file-management';
import { useFileService } from '@actschurch/ccms-sdk';
import { StorageStatistics } from '../types';

export const StorageStatsTab: React.FC = () => {
  const fileService = useFileService();
  const [stats, setStats] = useState<StorageStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await fileService.getStatistics();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [fileService]);

  if (loading || !stats) {
    return <Text>Loading statistics...</Text>;
  }

  return (
    <Stack gap="md">
      {/* Overview Cards */}
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <StorageStatCard
            title="Total Files"
            value={stats.totalFiles.toLocaleString()}
            gradientVariant="primary"
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <StorageStatCard
          title="Total Storage"
          value={stats.totalSizeFormatted}
          gradientVariant="secondary"
        />
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <StorageStatCard
            title="Average File Size"
            value={stats.averageFileSizeFormatted}
            gradientVariant="tertiary"
          />
        </Grid.Col>
      </Grid>

      {/* Domain Statistics */}
      {stats.byDomain && stats.byDomain.length > 0 && (
        <Card p="md" variant="outlined">
          <Stack gap="md">
            <Text fw={600}>Storage by Domain</Text>
            <Table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Files</th>
                  <th>Size</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {stats.byDomain.map((domain: { domain: string; fileCount: number; totalSizeFormatted: string; percentage: number }) => (
                  <tr key={domain.domain}>
                    <td><Text fw={500}>{domain.domain}</Text></td>
                    <td>{domain.fileCount.toLocaleString()}</td>
                    <td>{domain.totalSizeFormatted}</td>
                    <td>
                      <Group gap="xs">
                        <RingProgress
                          size={40}
                          thickness={4}
                          sections={[{ value: domain.percentage, color: 'blue' }]}
                        />
                        <Text size="sm">{domain.percentage.toFixed(1)}%</Text>
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Stack>
        </Card>
      )}

      {/* File Type Statistics */}
      {stats.byFileType && stats.byFileType.length > 0 && (
        <Card p="md" variant="outlined">
          <Stack gap="md">
            <Text fw={600}>Files by Type</Text>
            <Table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Count</th>
                  <th>Size</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {stats.byFileType.map((type: { fileType: string; count: number; totalSizeFormatted: string; percentage: number }) => (
                  <tr key={type.fileType}>
                    <td>
                      <Badge variant="light">{type.fileType}</Badge>
                    </td>
                    <td>{type.count.toLocaleString()}</td>
                    <td>{type.totalSizeFormatted}</td>
                    <td>{type.percentage.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Stack>
        </Card>
      )}

      {/* Top Uploaders */}
      {stats.topUploaders && stats.topUploaders.length > 0 && (
        <Card p="md" variant="outlined">
          <Stack gap="md">
            <Text fw={600}>Top Uploaders</Text>
            <Table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Files</th>
                  <th>Total Size</th>
                </tr>
              </thead>
              <tbody>
                {stats.topUploaders.map((uploader: { userId: string; userName?: string; userEmail?: string; fileCount: number; totalSizeFormatted: string }) => (
                  <tr key={uploader.userId}>
                    <td>
                      <Text fw={500}>{uploader.userName || uploader.userEmail}</Text>
                    </td>
                    <td>{uploader.fileCount.toLocaleString()}</td>
                    <td>{uploader.totalSizeFormatted}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
